// What a questionnaire step makes, shown on its plan card. Before approval:
// the exact questions, or the outline the platform drafts the questions from,
// with the images it will show. After it runs: the drafted questions and a way
// into the Survey Builder to read and edit them.
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import config from "../../config";
import { getUserToken } from "../../utils/helpers";
import { describeLogic } from "../../utils/surveyLogic";
import { toDisplayBrand } from "../../utils/displayBrand";
import { FIG_INK, FIG_MUTED, HAIRLINE } from "../../design";
import { TextButton, SmallButton, Spinner } from "./CardStyle";
import { AttachmentThumb } from "./Attachments";

export const QUESTIONNAIRE_ACTIONS = new Set(["survey.create", "survey.import_questions"]);

// Coming back to this tab (from the Survey Builder in another one): every
// questionnaire view and check on the page reloads, so nothing shown is older
// than the person's own edits. One listener for the page, many subscribers.
const returnListeners = new Set();
let lastReturn = 0;
const fireReturn = () => {
  if (document.visibilityState === "hidden") return;
  const now = Date.now();
  if (now - lastReturn < 1500) return;
  lastReturn = now;
  for (const fn of [...returnListeners]) { try { fn(); } catch (_) { /* one view failing never stops the rest */ } }
};
if (typeof window !== "undefined") {
  window.addEventListener("focus", fireReturn);
  document.addEventListener("visibilitychange", fireReturn);
}
export function useOnReturn(fn) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const call = () => ref.current && ref.current();
    returnListeners.add(call);
    return () => { returnListeners.delete(call); };
  }, []);
}

// The Survey Builder opens in a new tab, so the conversation stays where it is.
const openBuilder = (surveyId) => window.open(`${process.env.PUBLIC_URL || ""}/surveys/study/${surveyId}`, "_blank", "noopener");

const Box = styled.div`
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid ${HAIRLINE};
  border-radius: 10px;
  background: #fbfaf9;
  font-size: 12.5px;
  line-height: 1.5;
  color: #4e4b48;
  .lead { font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: ${FIG_MUTED}; margin-bottom: 6px; }
  .note { font-size: 12px; color: ${FIG_MUTED}; margin: 6px 0 0; }
  .part + .part { margin-top: 8px; }
  .part b { color: ${FIG_INK}; font-weight: 600; }
  ul, ol { margin: 2px 0 0; padding-left: 18px; }
  ul { list-style: disc; }
  ol { list-style: decimal; }
  li { display: list-item; }
  li + li { margin-top: 2px; }
  .kind { color: ${FIG_MUTED}; }
  .pics { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
  .acts { display: flex; align-items: center; gap: 10px; margin-top: 8px; flex-wrap: wrap; }
  .q + .q { margin-top: 10px; padding-top: 10px; border-top: 1px solid ${HAIRLINE}; }
  .q .t { color: ${FIG_INK}; font-weight: 500; }
  .q .n { color: ${FIG_MUTED}; font-weight: 600; margin-right: 6px; }
  .q .meta { font-size: 11.5px; color: ${FIG_MUTED}; margin-top: 2px; }
  .q .opts { margin-top: 3px; }
  .q .rule { font-size: 12px; margin-top: 3px; }
  .q .rule.out { color: #9B3F33; }
  .q .rule.probe { color: #4e4b48; }
  .warn { font-size: 12px; color: #9A4A06; margin-top: 8px; }
  .bad { font-size: 12px; color: #9B3F33; margin-top: 8px; }
`;

// ";" and sentence breaks split a section into items, except inside quotes or
// brackets; "H1 "..." H2 "..."" lists split before each coded item.
function splitItems(text) {
  const out = [];
  let cur = "";
  let depth = 0;
  let quote = null;
  const s = String(text || "");
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (quote) {
      cur += ch;
      if ((quote === '"' && ch === '"') || (quote === "“" && ch === "”")) quote = null;
      continue;
    }
    if (ch === '"' || ch === "“") { quote = ch; cur += ch; continue; }
    if (ch === "(" || ch === "[") depth += 1;
    if ((ch === ")" || ch === "]") && depth) depth -= 1;
    if (!depth && ch === ";") { out.push(cur); cur = ""; continue; }
    if (!depth && ch === "." && /^\s+[A-Z]/.test(s.slice(i + 1, i + 3)) && !/\b(?:vs|e\.g|i\.e|etc|approx)$/i.test(cur)) { out.push(`${cur}.`); cur = ""; continue; }
    if (!depth && /\s/.test(ch) && /^\s*[A-Z]\d{1,2}\s+["“]/.test(s.slice(i)) && cur.trim()) { out.push(cur); cur = ""; continue; }
    cur += ch;
  }
  out.push(cur);
  return out.map((x) => x.trim().replace(/^and\s+/i, "")).filter(Boolean);
}

// SCREENER -> Screener; codes like S1, NAD+ and US stay as written.
const tidyHead = (h) => h.trim().split(/\s+/).map((w) => (/^[A-Z]{4,}$/.test(w) ? w[0] + w.slice(1).toLowerCase() : w)).join(" ");

/** A brief as parts: a short heading before the first colon, then its items. */
export function outlineOf(brief) {
  return String(brief || "").split(/\n+/).map((l) => l.trim()).filter(Boolean).map((line) => {
    const m = line.match(/^([^:"“(]{2,90}):\s+(.+)$/);
    return m ? { head: tidyHead(m[1]), items: splitItems(m[2]) } : { head: null, items: [line] };
  });
}

const TYPE_WORDS = {
  single_choice: "pick one", multi_choice: "pick any", dropdown: "pick one", rating_scale: "scale", likert: "agree scale",
  nps: "0 to 10", ranking: "rank", grid_single: "grid", grid_multi: "grid", numeric: "number", date: "date",
  consent: "yes or no", maxdiff: "best and worst", price_vw: "price", info: "text only", open_text: "open answer", short_text: "short answer",
};
const kindOf = (q) => {
  const t = q.question_type || q.type;
  if (!t || t === "qualitative") return "open answer";
  const word = TYPE_WORDS[t] || String(t).replace(/_/g, " ");
  const opts = (q.options || []).map((o) => o && (o.label || o.code)).filter(Boolean);
  if (opts.length) return `${word}: ${opts.slice(0, 5).join(" / ")}${opts.length > 5 ? ` and ${opts.length - 5} more` : ""}`;
  if (q.scale && q.scale.min != null && q.scale.max != null) return `${word} ${q.scale.min} to ${q.scale.max}`;
  return word;
};

const mediaList = (params) => {
  const top = Array.isArray(params.media) ? params.media : params.media ? [params.media] : [];
  const perQ = (params.questions || []).map((q) => q && q.media).filter(Boolean);
  return [...top, ...perQ].filter((m) => m && (m.attachment_id || m.url));
};

function Media({ items }) {
  if (!items.length) return null;
  const files = items.filter((m) => m.attachment_id);
  const links = items.filter((m) => !m.attachment_id && m.url);
  return (
    <div className="part">
      <b>Shown to respondents</b>
      {files.length ? (
        <div className="pics">
          {files.map((m) => <AttachmentThumb key={m.attachment_id} fileId={m.attachment_id} name={m.label} caption={m.label} width={88} />)}
        </div>
      ) : null}
      {links.map((m) => <div key={m.url}>{toDisplayBrand(m.label || "Link")}: {m.url}</div>)}
      <p className="note">On screen in the web call and the web form. Someone who dials in by phone does not see it.</p>
    </div>
  );
}

/** Before approval: the questions, or the outline they are drafted from. */
export function QuestionnaireOutline({ params, compact }) {
  const [open, setOpen] = useState(!compact);
  const [all, setAll] = useState(false);
  const p = params || {};
  const questions = Array.isArray(p.questions) ? p.questions.filter(Boolean) : [];
  const parts = questions.length ? [] : outlineOf(p.brief);
  const media = mediaList(p);
  if (!questions.length && !parts.length && !media.length) return null;
  const minutes = Number(p.estimated_minutes) > 0 ? `, about ${p.estimated_minutes} minutes` : "";
  const label = questions.length ? `The ${questions.length} questions${minutes}` : `What the questionnaire covers${minutes}`;
  if (!open) return <TextButton type="button" onClick={() => setOpen(true)}>Show the questionnaire</TextButton>;
  const SHOW = 4;
  return (
    <Box>
      <div className="lead">{label}</div>
      {questions.length ? (
        <ol>
          {(all ? questions : questions.slice(0, 6)).map((q, i) => (
            <li key={i}>{toDisplayBrand(q.text || q.question_text || "")} <span className="kind">({kindOf(q)})</span></li>
          ))}
        </ol>
      ) : (
        (all ? parts : parts.slice(0, SHOW)).map((part, i) => (
          <div className="part" key={i}>
            {part.head ? <b>{toDisplayBrand(part.head)}</b> : null}
            {part.head && part.items.length > 1 ? (
              <ul>{part.items.map((t, j) => <li key={j}>{toDisplayBrand(t)}</li>)}</ul>
            ) : (
              <div>{toDisplayBrand(part.items.join(" "))}</div>
            )}
          </div>
        ))
      )}
      {(questions.length > 6 || parts.length > SHOW) ? (
        <div className="acts">
          <TextButton type="button" onClick={() => setAll((v) => !v)}>
            {all ? "Show less" : questions.length ? `Show all ${questions.length} questions` : `Show the full outline (${parts.length} parts)`}
          </TextButton>
        </div>
      ) : null}
      {media.length ? <div style={{ marginTop: 10 }}><Media items={media} /></div> : null}
      {questions.length ? null : (
        <p className="note">The platform writes the questions from this outline when you approve. You can read and edit every question in the Survey Builder before anything launches.</p>
      )}
      {compact ? <div className="acts"><TextButton type="button" onClick={() => setOpen(false)}>Hide</TextButton></div> : null}
    </Box>
  );
}

/** After it ran: the drafted questions, and the way into the Survey Builder. */
export function QuestionnaireResult({ result, compact }) {
  const [all, setAll] = useState(false);
  const r = result || {};
  const questions = Array.isArray(r.questions) ? r.questions.filter(Boolean) : [];
  const id = r.survey_id || r.id;
  if (!questions.length && !id) return null;
  if (id) return <QuestionnaireView surveyId={String(id)} compact={compact} label="Drafted questionnaire" />;
  return (
    <Box>
      <div className="lead">{questions.length ? `${questions.length} questions drafted` : "Questionnaire saved"}</div>
      {questions.length ? (
        <ol>{(all ? questions : questions.slice(0, 5)).map((t, i) => <li key={i}>{toDisplayBrand(String(t))}</li>)}</ol>
      ) : null}
      <div className="acts">
        {questions.length > 5 ? <TextButton type="button" onClick={() => setAll((v) => !v)}>{all ? "Show fewer" : `Show all ${questions.length} questions`}</TextButton> : null}
        {id ? <SmallButton type="button" onClick={() => openBuilder(id)}>Open in the Survey Builder</SmallButton> : null}
      </div>
    </Box>
  );
}

// ── The exact questionnaire, as respondents will get it ──────────────────

// One copy per questionnaire for the page: a card that remounts (the live
// answer swapped for the saved one) renders from it at once, and a card
// mounted while a load is in flight shares that load. Older than a minute,
// it is shown at once and refreshed behind.
const previewCache = new Map();
const PREVIEW_FRESH = 60000;
const loadPreview = (surveyId, { force = false } = {}) => {
  const hit = previewCache.get(surveyId);
  if (hit && hit.pending && !force) return hit.pending;
  const pending = axios.get(`${config.api.base_url}/surveys/${surveyId}/definition/preview`, { headers: { Authorization: `Bearer ${getUserToken()}` } })
    .then((r) => {
      const data = r?.data?.data || null;
      if (data) previewCache.set(surveyId, { at: Date.now(), data });
      else previewCache.delete(surveyId);
      return { data, error: data ? null : "The questionnaire could not be loaded." };
    })
    .catch((e) => {
      const prev = previewCache.get(surveyId);
      if (prev && prev.data) { previewCache.set(surveyId, { at: prev.at, data: prev.data }); return { data: prev.data, error: null }; }
      previewCache.delete(surveyId);
      return { data: null, error: e?.response?.status === 404 ? "This questionnaire no longer exists." : "The questionnaire could not be loaded." };
    });
  previewCache.set(surveyId, { ...(hit || {}), pending });
  return pending;
};
function useQuestionnaire(surveyId) {
  const cached = surveyId ? previewCache.get(surveyId) : null;
  const [state, setState] = useState(cached && cached.data
    ? { loading: false, data: cached.data, error: null }
    : { loading: !!surveyId, data: null, error: null });
  useEffect(() => {
    if (!surveyId) return undefined;
    let alive = true;
    const hit = previewCache.get(surveyId);
    if (hit && hit.data) {
      setState((st) => (st.data === hit.data ? st : { loading: false, data: hit.data, error: null }));
      if (hit.at > Date.now() - PREVIEW_FRESH) return undefined;
    } else {
      setState((st) => (st.loading ? st : { ...st, loading: true }));
    }
    loadPreview(surveyId).then((out) => {
      if (!alive) return;
      setState((st) => (out.data && st.data === out.data ? st : { loading: false, data: out.data, error: out.error }));
    });
    return () => { alive = false; };
  }, [surveyId]);
  // Back from the Survey Builder: load the questionnaire as it is now.
  useOnReturn(() => {
    if (!surveyId) return;
    loadPreview(surveyId, { force: true }).then((out) => {
      if (out.data) setState((st) => (st.data && st.data.hash === out.data.hash ? st : { loading: false, data: out.data, error: null }));
    });
  });
  return state;
}

// The check as the questionnaire is now (GET /surveys/:id/check), shared per
// page like the preview, reloaded on return to the tab.
const checkCache = new Map();
const loadCheck = (surveyId, { force = false } = {}) => {
  const hit = checkCache.get(surveyId);
  if (hit && hit.pending && !force) return hit.pending;
  if (hit && hit.data && !force && hit.at > Date.now() - PREVIEW_FRESH) return Promise.resolve(hit.data);
  const pending = axios.get(`${config.api.base_url}/surveys/${surveyId}/check`, { headers: { Authorization: `Bearer ${getUserToken()}` } })
    .then((r) => { const data = r?.data?.data || null; checkCache.set(surveyId, { at: Date.now(), data }); return data; })
    .catch(() => { checkCache.delete(surveyId); return null; });
  checkCache.set(surveyId, { ...(hit || {}), pending });
  return pending;
};
function useLiveChecks(ids) {
  const key = ids.join(",");
  const [live, setLive] = useState({});
  useEffect(() => {
    let alive = true;
    ids.forEach((id) => loadCheck(id).then((c) => { if (alive && c) setLive((m) => ({ ...m, [id]: c })); }));
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useOnReturn(() => { ids.forEach((id) => loadCheck(id, { force: true }).then((c) => { if (c) setLive((m) => ({ ...m, [id]: c })); })); });
  return live;
}
// Two checks say the same when they agree on the verdict and the findings.
const sameCheck = (a, b) => !!a && !!b && (a.hash && b.hash ? a.hash === b.hash
  : a.passes === b.passes && (a.problem_count || 0) === (b.problem_count || 0) && (a.quality_count || 0) === (b.quality_count || 0));

const optionText = (o) => `${o.label}${o.exclusive ? " (only answer)" : ""}${o.other_specify ? " (with a text box)" : ""}`;

function answerLine(q) {
  const t = q.question_type;
  if (t === "info") return "Shown as text, no answer";
  if (t === "consent") return "Yes or no";
  const word = TYPE_WORDS[t] || (t ? String(t).replace(/_/g, " ") : "open answer");
  const bits = [];
  if (q.scale && q.scale.min != null && q.scale.max != null) {
    bits.push(`${q.scale.min}${q.scale.min_label ? ` ${q.scale.min_label}` : ""} to ${q.scale.max}${q.scale.max_label ? ` ${q.scale.max_label}` : ""}`);
  }
  if (q.grid && (q.grid.rows || []).length) bits.push(`${q.grid.rows.length} rows: ${q.grid.rows.map((r) => r.label).join(" / ")}`);
  const v = q.validation || {};
  if (t === "multi_choice" && (v.min_select || v.max_select)) bits.push(v.max_select ? `up to ${v.max_select}` : `at least ${v.min_select}`);
  if (q.randomization && q.randomization.options && q.randomization.options !== "none") bits.push("options in random order");
  return [word.charAt(0).toUpperCase() + word.slice(1), ...bits].join(", ");
}

function mediaOfQuestion(q) {
  const all = [q.media, ...(q.followups || []).map((f) => f && f.media), ...(q.rule_followups || []).map((f) => f && f.media)];
  return all.filter((m) => m && m.media_type);
}

/**
 * The exact questionnaire of a survey: every question in order with its
 * answers, screen-outs, skips, follow-ups and media, read from the same
 * frozen form respondents get. surveyId is the questionnaire.
 */
export function QuestionnaireView({ surveyId, compact, label = "The questionnaire", limit = 8 }) {
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState(!compact);
  const { loading, data, error } = useQuestionnaire(open ? surveyId : null);
  if (!surveyId) return null;
  if (!open) return <TextButton type="button" onClick={() => setOpen(true)}>Show the questionnaire</TextButton>;
  if (loading) return <Box><span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Spinner $size={12} /> Loading the questionnaire</span></Box>;
  if (error || !data) return <Box><span className="bad">{error || "The questionnaire could not be loaded."}</span></Box>;
  const def = data.definition || {};
  const qs = (def.questions || []).filter(Boolean);
  const title = def.survey && (def.survey.title || def.survey.name);
  const pos = new Map(qs.map((q, i) => [q.question_key, i + 1]));
  const byKey = new Map(qs.map((q) => [q.question_key, q]));
  const ctxFor = (self) => ({
    labelOf: (ref) => {
      const key = String(ref || "").replace(/^q:/, "");
      if (key === self) return "the answer";
      return pos.has(key) ? `Q${pos.get(key)}` : null;
    },
    optionLabel: (key, code) => {
      const q = byKey.get(key);
      if (q && q.question_type === "consent") return { 1: "yes", 0: "no", true: "yes", false: "no" }[String(code)] || null;
      const o = q && (q.options || []).find((x) => String(x.code) === String(code));
      return o ? o.label : null;
    },
  });
  const goText = (g, at) => (g === "__end_terminate" ? "screened out" : g === "__end_complete" ? "the survey ends" : g === "__end_quota" ? "over quota"
    : pos.has(g) ? (pos.get(g) === at + 1 ? `continues to Q${pos.get(g)}` : `skips to Q${pos.get(g)}`) : `goes to ${g}`);
  const closed = qs.filter((q) => q.question_type && !["open_text", "short_text", "info"].includes(q.question_type)).length;
  const shown = all ? qs : qs.slice(0, limit);
  const problems = data.problems || [];
  return (
    <Box>
      <div className="lead">{label}{title ? `: ${toDisplayBrand(title)}` : ""} · {qs.length} questions{closed ? `, ${closed} closed` : ""}</div>
      {shown.map((q, i) => {
        const ctx = ctxFor(q.question_key);
        const opts = (q.options || []).filter((o) => !o.hidden);
        const rules = (q.skip_logic || []).filter((r) => r && r.goto);
        const media = mediaOfQuestion(q);
        return (
          <div className="q" key={q.question_key || i}>
            <div><span className="n">Q{i + 1}</span><span className="t">{toDisplayBrand(q.question_text || "")}</span></div>
            <div className="meta">{answerLine(q)}{q.validation && q.validation.required === "hard" ? " · required" : ""}</div>
            {opts.length ? <div className="opts">{opts.map(optionText).join(" · ")}</div> : null}
            {q.display_logic ? <div className="rule">Asked only if {describeLogic(q.display_logic, ctx)}.</div> : null}
            {rules.map((r, j) => (
              <div key={j} className={`rule${r.goto === "__end_terminate" ? " out" : ""}`}>
                {r.when ? `If ${describeLogic(r.when, ctx)}: ${goText(r.goto, i + 1)}.` : `Anyone else: ${goText(r.goto, i + 1)}.`}
              </div>
            ))}
            {(q.rule_followups || []).map((f, j) => (
              <div key={`r${j}`} className="rule probe">
                Follow-up {f.when ? `if ${describeLogic(f.when, ctx)}` : f.condition ? `if ${f.condition}` : ""}: {toDisplayBrand(f.text || (f.probe_mode === "ai" ? "asked by the AI interviewer" : ""))}{f.probe_mode === "ai" ? " (AI probe)" : ""}
              </div>
            ))}
            {(q.followups || []).map((f, j) => <div key={`f${j}`} className="rule probe">Follow-up: {toDisplayBrand(f.text || "")}</div>)}
            {media.length ? (
              <div className="pics">
                {media.map((m, j) => (m.file_id && m.media_type === "image"
                  ? <AttachmentThumb key={j} fileId={m.file_id} name={m.label || m.filename} caption={m.label || m.filename} width={88} />
                  : <span key={j} className="meta">Shows {m.media_type === "link" ? "a link" : `a ${m.media_type}`}: {m.label || m.filename || m.url}</span>))}
              </div>
            ) : null}
          </div>
        );
      })}
      {problems.length ? <div className="bad">Must be fixed before launch: {problems.slice(0, 3).join("; ")}</div> : null}
      <div className="acts">
        {qs.length > limit ? <TextButton type="button" onClick={() => setAll((v) => !v)}>{all ? "Show fewer" : `Show all ${qs.length} questions`}</TextButton> : null}
        <SmallButton type="button" onClick={() => openBuilder(surveyId)}>Open in the Survey Builder</SmallButton>
        {compact ? <TextButton type="button" onClick={() => setOpen(false)}>Hide</TextButton> : null}
      </div>
    </Box>
  );
}

// ── The questionnaire check a run ends with ──────────────────────────────

const CheckBox = styled.div`
  margin-top: 8px;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 12.5px;
  line-height: 1.5;
  border: 1px solid ${(p) => (p.$tone === "red" ? "#F0D2CC" : p.$tone === "green" ? "#CFE6DE" : p.$tone === "amber" ? "#F2DDBF" : HAIRLINE)};
  background: ${(p) => (p.$tone === "red" ? "#FBF1EF" : p.$tone === "green" ? "#F1F8F5" : p.$tone === "amber" ? "#FDF6EC" : "#fbfaf9")};
  color: #4e4b48;
  b { color: ${(p) => (p.$tone === "red" ? "#9B3F33" : p.$tone === "green" ? "#22665A" : p.$tone === "amber" ? "#9A4A06" : FIG_INK)}; font-weight: 600; }
  .sub { margin-top: 6px; font-weight: 600; color: #9A4A06; }
  ul { margin: 4px 0 0; padding-left: 18px; list-style: disc; }
  li { display: list-item; }
  li + li { margin-top: 2px; }
  .acts { display: flex; align-items: center; gap: 10px; margin-top: 8px; flex-wrap: wrap; }
`;

/**
 * What the questionnaire check found when a run finished (plan.questionnaire_check):
 * passes, or the problems to fix before launch with a way to have them fixed.
 */
export function QuestionnaireCheckNote({ checks, onRevise }) {
  const [notes, setNotes] = useState(false);
  const stored = (Array.isArray(checks) ? checks : []).filter(Boolean);
  const live = useLiveChecks(stored.map((c) => c.survey_id).filter((id) => typeof id === "string" && /^[a-f0-9]{24}$/i.test(id)));
  if (!stored.length) return null;
  // The check as the questionnaire is now: after edits in the Survey Builder
  // the receipt shows the person's version, and says so.
  const list = stored.map((c) => {
    const now = c.survey_id ? live[c.survey_id] : null;
    if (!now || c.error || sameCheck(c, now)) return c;
    return { ...now, title: now.title || c.title, rechecked: true };
  });
  return list.map((c, i) => {
    const name = c.title ? `"${toDisplayBrand(c.title)}"` : "the questionnaire";
    const again = c.rechecked ? <div style={{ fontSize: 11.5, color: FIG_MUTED, marginBottom: 4 }}>Checked again after edits in the Survey Builder</div> : null;
    if (c.error) {
      return <CheckBox key={c.survey_id || i}><b>Questionnaire check could not run</b> for {name}. Ask Copilot to check it again before launch.</CheckBox>;
    }
    const builder = c.survey_id ? <SmallButton type="button" onClick={() => openBuilder(c.survey_id)}>Open in the Survey Builder</SmallButton> : null;
    const quality = c.quality || [];
    const qn = c.quality_count || quality.length;
    const fixText = (items) => `Fix the questionnaire ${c.title ? `"${c.title}"` : ""} before launch: ${items.join("; ")}`;
    if (c.passes && quality.length) {
      return (
        <CheckBox key={c.survey_id || i} $tone="amber" role="status">
          {again}
          <b>Questionnaire check passed, with {qn} quality {qn === 1 ? "issue" : "issues"} to fix before launch</b> in {name}
          <ul>{quality.map((t, j) => <li key={j}>{toDisplayBrand(t)}</li>)}</ul>
          {qn > quality.length ? <div>and {qn - quality.length} more</div> : null}
          <div className="acts">
            {onRevise ? <SmallButton type="button" onClick={() => onRevise(fixText(quality))}>Fix it with Copilot</SmallButton> : null}
            {builder}
          </div>
        </CheckBox>
      );
    }
    if (c.passes) {
      const w = c.warnings || [];
      return (
        <CheckBox key={c.survey_id || i} $tone="green" role="status">
          {again}
          <b>Questionnaire check passed</b> for {name}: {c.question_count} questions, nothing blocks a launch.
          {w.length ? (
            <>
              {" "}<TextButton type="button" onClick={() => setNotes((v) => !v)}>{notes ? "Hide notes" : `${c.warning_count || w.length} ${w.length === 1 ? "note" : "notes"}`}</TextButton>
              {notes ? <ul>{w.map((t, j) => <li key={j}>{toDisplayBrand(t)}</li>)}</ul> : null}
            </>
          ) : null}
        </CheckBox>
      );
    }
    const probs = c.problems || [];
    const n = c.problem_count || probs.length;
    return (
      <CheckBox key={c.survey_id || i} $tone="red" role="alert">
        {again}
        <b>Questionnaire check: {n} {n === 1 ? "problem" : "problems"} to fix before launch</b> in {name}
        <ul>{probs.map((t, j) => <li key={j}>{toDisplayBrand(t)}</li>)}</ul>
        {n > probs.length ? <div>and {n - probs.length} more</div> : null}
        {quality.length ? (
          <>
            <div className="sub">Also worth fixing ({qn})</div>
            <ul>{quality.map((t, j) => <li key={j}>{toDisplayBrand(t)}</li>)}</ul>
          </>
        ) : null}
        <div className="acts">
          {onRevise ? <SmallButton type="button" onClick={() => onRevise(fixText([...probs, ...quality]))}>Fix it with Copilot</SmallButton> : null}
          {builder}
        </div>
      </CheckBox>
    );
  });
}
